import { useId, type CSSProperties } from 'react';
import type { BulleEnrichie, TraitDeCarte, TransversaleDeFeuille } from '../partage/api/types';
import { dessinDuLien } from './dessinDesLiens';
import { courbe, milieuDeLaCourbe, pointeDeFleche, type Point } from './geometrieCarte';
import type { EtiquetteDuDehors } from './placeDesEtiquettes';
import type { TailleDessinee } from './taillesDesBulles';

/**
 * Les traits de la carte — et ils disent CINQ choses différentes (design/carte.html).
 *
 * C'est la seule raison de ce fichier : un trait n'est pas une décoration, c'est une phrase.
 * Le gris le plus pâle pour ce qu'on n'a jamais ouvert, le gris franc pour ce qui vit mais
 * reste replié, le bleu-vert pâle pour le chemin creusé, le bleu-vert plein pour la branche
 * où l'on vient de descendre. La cinquième — la transversale — se dessine à part : elle ne
 * part pas du centre, elle traverse, et la planche la veut violette et pointillée pour qu'on
 * ne la confonde jamais avec une descente.
 *
 * Depuis le ticket 06 de retours-testeurs, chaque trait prend la couleur de l'axe de sa
 * bulle, et une étape ouverte sur place relie ses bulles à elle par le même trait. Un trait
 * caché se replie dans la mère, et en ressort avec elle.
 * Les traits glissent avec les bulles par la propriété CSS `d` (l'attribut les dessine là
 * où elle n'est pas comprise, ticket 35).
 *
 * **Aucun trait ne se voit sur une bulle** (ticket 25). Un trait part du milieu d'une bulle
 * et en croise parfois d'autres en chemin. Sous une bulle blanche, il disparaît de lui-même ;
 * mais une bulle acquise est transparente (une « jamais ouverte » l'était aussi avant le
 * retour 4 des testeurs, ticket 03), et le trait rayait son titre. Un masque efface donc
 * tous les traits là où une bulle est posée : ils passent dessous et s'arrêtent à son bord,
 * à sa taille mesurée à l'écran.
 *
 * Depuis le chantier liens-entre-bulles (ticket 03), les liens ont trois dessins (`dessinDuLien`) :
 * « attend » pointillé et fléché, « sert à » plein et fléché, « touche à » plein sans flèche ; un
 * « attend » rempli et un lien caché montré par A en pâle. La flèche se pose au milieu du trait (à
 * son bout, elle serait sous la bulle). Un trait qui porte un lien se clique (`ZonesDesLiens`).
 */
export function TraitsCarte({
  bulles,
  centreId,
  placeDe,
  visibles,
  tailles,
  liens,
  etiquettes,
  ouverteId,
  axeDe,
  designe = null,
}: {
  /** Toutes les bulles de la carte, visibles ou non. */
  bulles: BulleEnrichie[];
  centreId: string;
  /** La couleur d'axe d'une bulle, `var(--axe-N)`, que son trait prend (ticket 06). */
  axeDe: (id: string) => string | null;
  /** Sa place, ou celle de la bulle visible qui la contient quand elle est cachée. */
  placeDe: (id: string) => Point;
  visibles: Set<string>;
  /** La taille de chaque bulle à l'écran : là où les traits s'effacent. */
  tailles: Record<string, TailleDessinee>;
  /** Les liens à dessiner entre bulles visibles, de toutes les sortes. */
  liens: TraitDeCarte[];
  /** Où se posent les étiquettes de ceux qui sortent de la feuille. */
  etiquettes: EtiquetteDuDehors<TransversaleDeFeuille>[];
  ouverteId: string | null;
  /** Le lien désigné par un clic : son trait s'épaissit. */
  designe?: string | null;
}) {
  const centre = { x: 0, y: 0 };
  const masque = `tableau-masque-${useId().replace(/[^\w-]/g, '')}`;

  return (
    <svg className="tableau-traits" width={1} height={1} aria-hidden="true">
      <defs>
        {/* En unités du monde, et assez grand pour la carte la plus touffue : par défaut, la
            zone d'un masque suit la boîte de ce qu'il masque, et un trait tout droit n'a
            pas de hauteur. Blanc et noir ne sont pas des couleurs à l'écran : le blanc garde
            le trait, le noir l'efface. */}
        <mask
          id={masque}
          maskUnits="userSpaceOnUse"
          x={-ETENDUE}
          y={-ETENDUE}
          width={2 * ETENDUE}
          height={2 * ETENDUE}
        >
          <rect x={-ETENDUE} y={-ETENDUE} width={2 * ETENDUE} height={2 * ETENDUE} fill="white" />
          {bulles.map((bulle) => (
            <PlaceDUneBulle
              key={bulle.id}
              place={placeDe(bulle.id)}
              taille={visibles.has(bulle.id) ? tailles[bulle.id] : undefined}
            />
          ))}
        </mask>
      </defs>

      <g mask={`url(#${masque})`}>
        {/* Un lien de chaque bulle vers sa mère : c'est la condition verticale, dessinée. */}
        {bulles.map((bulle) => {
          const premier = bulle.parentId === centreId;
          const depart = premier ? centre : placeDe(bulle.parentId ?? centreId);
          const d = courbe(depart, placeDe(bulle.id));
          const axe = axeDe(bulle.id);
          const famille = familleDuLien(bulle, bulle.id === ouverteId, axe !== null);
          return (
            <path
              key={bulle.id}
              className={`trait-vers-la-mere${visibles.has(bulle.id) ? '' : ' tableau-trait--cache'}`}
              d={d}
              /* Son épaisseur suit le trait de l'habit du projet (`styles/bulles/trait.css`). */
              style={
                {
                  d: `path('${d}')`,
                  ['--epaisseur-du-lien' as string]: famille.epaisseur,
                  ...(axe ? { ['--axe' as string]: axe } : {}),
                } as CSSProperties
              }
              stroke={famille.trait}
              strokeWidth={famille.epaisseur}
              fill="none"
            />
          );
        })}

        {/* Les liens : violets, ils ne descendent pas, ils traversent ; chacun selon sa sorte. */}
        {liens.map((l) => (
          <Transversale
            key={cleDuTrait(l)}
            depart={placeDe(l.sourceId)}
            arrivee={placeDe(l.cibleId)}
            lien={l}
            designe={l.lienId !== null && l.lienId === designe}
          />
        ))}

        {/* Et ceux qui sortent de la feuille : le même trait, vers l'étiquette qui dit où il va.
            La flèche va de la première bulle vers l'autre : vers l'étiquette quand la bulle d'ici
            est la première, vers la bulle sinon. */}
        {etiquettes.map(({ depart, point, transversale }) => (
          <Transversale
            key={cleDuTrait(transversale)}
            depart={transversale.sens === 'entre' ? point : depart}
            arrivee={transversale.sens === 'entre' ? depart : point}
            lien={transversale}
            designe={transversale.lienId !== null && transversale.lienId === designe}
          />
        ))}
      </g>
    </svg>
  );
}

/** Au-delà, la carte n'ouvre plus d'étape bien avant (400 bulles visibles, `carte.ts`). */
const ETENDUE = 100_000;

/**
 * Le trou d'une bulle dans le masque. Il glisse avec elle (propriétés CSS `x`, `y`, `width`,
 * `height`, VueTableau.css) ; une bulle cachée n'en fait pas, elle attend sur sa mère et le
 * sien sort d'un point quand elle sort.
 */
function PlaceDUneBulle({ place, taille }: { place: Point; taille: TailleDessinee | undefined }) {
  const largeur = taille?.largeur ?? 0;
  const hauteur = taille?.hauteur ?? 0;
  const x = place.x - largeur / 2;
  const y = place.y - hauteur / 2;
  const style = { x, y, width: largeur, height: hauteur } as CSSProperties;
  return (
    <rect
      className="tableau-masque-bulle"
      x={x}
      y={y}
      width={largeur}
      height={hauteur}
      style={style}
      fill="black"
    />
  );
}

/** La clé d'un trait : sa sorte, sa visibilité, ses bouts. */
function cleDuTrait(l: Pick<TraitDeCarte, 'sorte' | 'visible' | 'sourceId' | 'cibleId'>): string {
  return `${l.sorte}-${l.visible ? 'v' : 'c'}-${l.sourceId}-${l.cibleId}`;
}

function Transversale({
  depart,
  arrivee,
  lien,
  designe,
}: {
  depart: Point;
  arrivee: Point;
  lien: Pick<TraitDeCarte, 'sorte' | 'visible' | 'retient' | 'lienId'>;
  designe: boolean;
}) {
  const d = courbe(depart, arrivee);
  const dessin = dessinDuLien(lien);
  const fleche = dessin.fleche ? pointeDeFleche(milieuDeLaCourbe(depart, arrivee)) : null;
  const classe = `tableau-lien${dessin.pale ? ' tableau-lien--pale' : ''}${designe ? ' tableau-lien--designe' : ''}`;
  return (
    <g className={classe} data-sorte={lien.sorte} data-lien={lien.lienId ?? undefined}>
      <path
        d={d}
        style={{ d: `path('${d}')` }}
        /* « attend » garde son pointillé violet pâle d'avant ; les deux autres, un trait plein. */
        stroke={dessin.pointille ? 'var(--transversale-pale)' : 'var(--transversale)'}
        strokeWidth={dessin.pointille ? 1.4 : lien.sorte === 'sert_a' ? 1.3 : 1}
        strokeDasharray={dessin.pointille ? '3 5' : undefined}
        fill="none"
      />
      {fleche && (
        <path
          className="tableau-lien-fleche"
          d={fleche}
          style={{ d: `path('${fleche}')` }}
          stroke="var(--transversale)"
          strokeWidth={1.4}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      )}
    </g>
  );
}

/**
 * Les zones où un lien se clique (chantier liens-entre-bulles, ticket 03) : un trait invisible,
 * plus large que le dessin, sur chaque lien qui en porte un seul (`lienId`). Un clic le désigne,
 * au milieu du trait, là où les signes s'ouvrent. Au-dessus des traits, sous les bulles.
 */
export function ZonesDesLiens({
  liens,
  etiquettes,
  placeDe,
  onDesigner,
}: {
  liens: TraitDeCarte[];
  etiquettes: EtiquetteDuDehors<TransversaleDeFeuille>[];
  placeDe: (id: string) => Point;
  onDesigner: (lien: TraitDeCarte | TransversaleDeFeuille, ou: Point) => void;
}) {
  const zones = [
    ...liens.map((l) => ({ lien: l as TraitDeCarte | TransversaleDeFeuille, depart: placeDe(l.sourceId), arrivee: placeDe(l.cibleId) })),
    ...etiquettes.map(({ depart, point, transversale }) => ({
      lien: transversale as TraitDeCarte | TransversaleDeFeuille,
      depart: transversale.sens === 'entre' ? point : depart,
      arrivee: transversale.sens === 'entre' ? depart : point,
    })),
  ].filter((z) => z.lien.lienId !== null);
  return (
    <svg className="tableau-liens-zones" width={1} height={1} aria-hidden="true">
      {zones.map(({ lien, depart, arrivee }) => {
        const d = courbe(depart, arrivee);
        return (
          <path
            key={`${lien.lienId}-${cleDuTrait(lien)}`}
            className="tableau-lien-zone carte-commandes"
            data-lien={lien.lienId ?? undefined}
            d={d}
            style={{ d: `path('${d}')` }}
            stroke="transparent"
            strokeWidth={ZONE_DU_CLIC}
            fill="none"
            onClick={(e) => {
              e.stopPropagation();
              onDesigner(lien, milieuDeLaCourbe(depart, arrivee).point);
            }}
          />
        );
      })}
    </svg>
  );
}

/** La largeur où un trait se clique, en pixels de la carte : bien plus que son dessin. */
const ZONE_DU_CLIC = 14;

/**
 * Sur la carte, le trait prend la couleur de l'axe de sa bulle (ticket 06 de
 * retours-testeurs), d'une seule épaisseur :
 * c'est la couleur qui dit à qui il est. La branche d'où l'on remonte le garde plein. Les
 * autres familles restent pour un trait sans axe.
 */
function familleDuLien(
  bulle: BulleEnrichie,
  ouverte: boolean,
  avecAxe: boolean
): { trait: string; epaisseur: number } {
  if (avecAxe) return { trait: ouverte ? 'var(--axe)' : 'var(--axe-trait)', epaisseur: ouverte ? 2 : 1.8 };
  if (ouverte) return { trait: 'var(--accent)', epaisseur: 2 };
  if (bulle.jamaisOuverte && bulle.etat === 'a_faire') {
    return { trait: 'var(--trait-charniere)', epaisseur: 1.3 };
  }
  if (bulle.etat === 'fait' || bulle.nbEnfants > 0) {
    return { trait: 'var(--accent-pale)', epaisseur: 2 };
  }
  return { trait: 'var(--trait-marque)', epaisseur: 1.6 };
}
